import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
// The pure entry loads Stripe.js only when loadStripe runs: a demo invoice never fetches it.
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  BrandedCard,
  Footer,
  InvoiceCard,
} from "@/components/invoice-layout";
import { WhatsAppOptIn } from "@/components/whatsapp-opt-in";
import { DemoCardForm } from "@/components/demo";
import {
  amountDueNow,
  createCheckoutSession,
  fetchInvoice,
  type CheckoutSessionResponse,
  type PublicApiError,
  type PublicInvoiceResponse,
} from "@/api/public";
import {
  isMockState,
  mockCheckoutSession,
  mockInvoice,
  type MockState,
} from "@/api/fixtures";
import {
  DOLFIN_ACCENT,
  isDarkEnoughForText,
  parseBrandColour,
  pickForeground,
  tintFor,
} from "@/lib/brand";
import { formatCurrency } from "@/lib/format";

const DEFAULT_TITLE = "Dolfin Pay";

type Stage =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "paid"; invoice: PublicInvoiceResponse }
  | { kind: "voided"; invoice: PublicInvoiceResponse }
  | { kind: "credited"; invoice: PublicInvoiceResponse }
  | { kind: "network-error" }
  | { kind: "initialising-checkout"; invoice: PublicInvoiceResponse }
  | { kind: "checkout-error"; invoice: PublicInvoiceResponse; message: string; retryable: boolean }
  | {
      kind: "payable";
      invoice: PublicInvoiceResponse;
      session: CheckoutSessionResponse;
      stripe: Promise<Stripe | null>;
    }
  | { kind: "mock-payable"; invoice: PublicInvoiceResponse; session: CheckoutSessionResponse }
  | { kind: "demo-payable"; invoice: PublicInvoiceResponse }
  | { kind: "succeeded"; invoice: PublicInvoiceResponse };

function invoiceFromStage(stage: Stage): PublicInvoiceResponse | null {
  return "invoice" in stage ? stage.invoice : null;
}

export function PaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  const mockParam = process.env.NODE_ENV !== "production" ? searchParams.get("mock") : null;
  const mockState: MockState | null = isMockState(mockParam) ? mockParam : null;

  const invoice = invoiceFromStage(stage);
  const brand = parseBrandColour(invoice?.organisationPrimaryColour) ?? DOLFIN_ACCENT;

  // A1: dynamic title — "Pay £124.50 — ACME Electric Ltd"
  useEffect(() => {
    if (!invoice) {
      document.title = DEFAULT_TITLE;
      return;
    }
    document.title = `Pay ${formatCurrency(amountDueNow(invoice), invoice.currency)} — ${invoice.organisationName}`;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [invoice?.amountDue, invoice?.earlyPaymentDiscount?.amountDueNow, invoice?.currency, invoice?.organisationName]);

  // A2: dynamic favicon — swap to the org logo when present, restore on unmount/change.
  useEffect(() => {
    if (!invoice?.organisationLogoUrl) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    const previous = link.href;
    link.href = invoice.organisationLogoUrl;
    return () => {
      link.href = previous;
    };
  }, [invoice?.organisationLogoUrl]);

  useEffect(() => {
    if (!token) {
      setStage({ kind: "invalid" });
      return;
    }

    let cancelled = false;

    (async () => {
      if (mockState) {
        resolveMock(mockState);
        return;
      }

      const result = await fetchInvoice(token);
      if (cancelled) return;
      if (!result.ok) {
        handleFetchError(result.error);
        return;
      }

      const inv = result.data;
      if (inv.status === "Paid") {
        setStage({ kind: "paid", invoice: inv });
        return;
      }
      if (inv.status === "Void") {
        setStage({ kind: "voided", invoice: inv });
        return;
      }
      // Settled entirely by credit notes — nothing left to pay.
      if (inv.status === "Credited" || inv.amountDue <= 0) {
        setStage({ kind: "credited", invoice: inv });
        return;
      }
      if (inv.isDemo) {
        setStage({ kind: "demo-payable", invoice: inv });
        return;
      }
      await initialiseCheckout(token, inv);
    })();

    function resolveMock(state: MockState) {
      if (state === "invalid") {
        setStage({ kind: "invalid" });
        return;
      }
      if (state === "network-error") {
        setStage({ kind: "network-error" });
        return;
      }
      const inv = mockInvoice(state);
      const sess = mockCheckoutSession(state);
      if (!inv || !sess) {
        setStage({ kind: "invalid" });
        return;
      }
      if (inv.status === "Paid") {
        setStage({ kind: "paid", invoice: inv });
        return;
      }
      if (inv.status === "Void") {
        setStage({ kind: "voided", invoice: inv });
        return;
      }
      if (inv.status === "Credited" || inv.amountDue <= 0) {
        setStage({ kind: "credited", invoice: inv });
        return;
      }
      setStage({ kind: "mock-payable", invoice: inv, session: sess });
    }

    function handleFetchError(error: PublicApiError) {
      switch (error.kind) {
        case "invalid-token":
        case "not-payable":
          // "not-payable" on the initial GET carries no invoice body to pick Paid/Void from, so show the generic invalid state.
          setStage({ kind: "invalid" });
          return;
        case "network":
          setStage({ kind: "network-error" });
          return;
        default:
          console.error("Unexpected fetch error", { token, error });
          setStage({ kind: "invalid" });
      }
    }

    async function initialiseCheckout(tkn: string, inv: PublicInvoiceResponse) {
      setStage({ kind: "initialising-checkout", invoice: inv });
      const result = await createCheckoutSession(tkn);
      if (cancelled) return;

      if (!result.ok) {
        const err = result.error;
        if (err.kind === "not-payable") {
          setStage({ kind: "voided", invoice: inv });
          return;
        }
        console.error("Checkout session error", { token: tkn, error: err });
        setStage({
          kind: "checkout-error",
          invoice: inv,
          message: problemMessage(err, inv.organisationName),
          // Retrying can't fix an organisation that takes no card payments.
          retryable: err.kind !== "account-disabled",
        });
        return;
      }

      const session = result.data;
      const stripe = loadStripe(session.publishableKey, {
        stripeAccount: session.connectedAccountId,
      });
      setStage({ kind: "payable", invoice: inv, session, stripe });
    }

    return () => {
      cancelled = true;
    };
  }, [token, mockState, reloadKey]);

  const reload = () => {
    setStage({ kind: "loading" });
    setReloadKey((k) => k + 1);
  };

  // A4 + A6: background tint and focus-ring colour, both driven by brand.
  const pageStyle = {
    backgroundColor: tintFor(brand),
    ["--ring" as string]: brand,
  } as CSSProperties;

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4 sm:p-6"
      style={pageStyle}
    >
      <div className="w-full max-w-md">
        <StageView
          stage={stage}
          token={token ?? ""}
          brand={brand}
          onReload={reload}
          onSucceeded={(inv) => setStage({ kind: "succeeded", invoice: inv })}
        />
        {invoice && token ? <WhatsAppOptIn key={token} token={token} invoice={invoice} mock={mockState !== null} /> : null}
        <Footer note={invoice?.isDemo ? "Demo payment. No money moves." : undefined} />
      </div>
    </div>
  );
}

function StageView({
  stage,
  token,
  brand,
  onReload,
  onSucceeded,
}: {
  stage: Stage;
  token: string;
  brand: string;
  onReload: () => void;
  onSucceeded: (invoice: PublicInvoiceResponse) => void;
}) {
  switch (stage.kind) {
    case "loading":
      return <LoadingCard />;
    case "invalid":
      return <InvalidCard />;
    case "network-error":
      return <NetworkErrorCard onRetry={onReload} />;
    case "paid":
      return <PaidCard invoice={stage.invoice} brand={brand} />;
    case "voided":
      return <VoidedCard invoice={stage.invoice} brand={brand} />;
    case "credited":
      return <CreditedCard invoice={stage.invoice} brand={brand} />;
    case "initialising-checkout":
      return (
        <InvoiceCard invoice={stage.invoice} brand={brand} amountTint={isDarkEnoughForText(brand) ? brand : undefined}>
          <InitialisingCheckout />
        </InvoiceCard>
      );
    case "checkout-error":
      return (
        <InvoiceCard invoice={stage.invoice} brand={brand} amountTint={isDarkEnoughForText(brand) ? brand : undefined}>
          <CheckoutErrorInline message={stage.message} onRetry={stage.retryable ? onReload : undefined} />
        </InvoiceCard>
      );
    case "payable":
      return (
        <InvoiceCard invoice={stage.invoice} brand={brand} amountTint={isDarkEnoughForText(brand) ? brand : undefined}>
          <Elements stripe={stage.stripe} options={elementsOptions(stage.session, brand)}>
            <PaymentForm
              token={token}
              invoice={stage.invoice}
              session={stage.session}
              brand={brand}
              onSucceeded={() => onSucceeded(stage.invoice)}
            />
          </Elements>
        </InvoiceCard>
      );
    case "mock-payable":
      return (
        <InvoiceCard invoice={stage.invoice} brand={brand} amountTint={isDarkEnoughForText(brand) ? brand : undefined}>
          <MockPaymentForm
            session={stage.session}
            brand={brand}
            onSucceeded={() => onSucceeded(stage.invoice)}
          />
        </InvoiceCard>
      );
    case "demo-payable":
      return (
        <InvoiceCard invoice={stage.invoice} brand={brand} amountTint={isDarkEnoughForText(brand) ? brand : undefined}>
          <DemoCardForm
            token={token}
            amount={amountDueNow(stage.invoice)}
            currency={stage.invoice.currency}
            brand={brand}
            onSucceeded={() => onSucceeded(stage.invoice)}
          />
        </InvoiceCard>
      );
    case "succeeded":
      return <SucceededCard invoice={stage.invoice} brand={brand} />;
  }
}

function elementsOptions(
  session: CheckoutSessionResponse,
  brand: string,
): StripeElementsOptions {
  return {
    clientSecret: session.clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: brand,
        borderRadius: "8px",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
      },
    },
  };
}

// A payer sees these, so never the backend's own detail: it is written for the organisation and can carry its ids.
function problemMessage(error: PublicApiError, organisationName: string): string {
  if (error.kind === "network")
    return "Couldn't reach the payment service. Check your connection and try again.";
  if (error.kind === "account-disabled")
    return `${organisationName} hasn't set up online payments yet. Please contact them to pay this invoice another way.`;
  return "We couldn't start the payment. Please try again.";
}

function LoadingCard() {
  return (
    <Card>
      <CardContent className="flex min-h-[260px] items-center justify-center">
        <Spinner />
      </CardContent>
    </Card>
  );
}

function InvalidCard() {
  return (
    <Card>
      <CardHeader className="items-center gap-3 text-center">
        <AlertTriangle className="h-10 w-10 text-muted-foreground" />
        <CardTitle>This payment link is no longer valid</CardTitle>
        <CardDescription>Ask whoever sent this invoice for a new link.</CardDescription>
      </CardHeader>
      <CardContent />
    </Card>
  );
}

function NetworkErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <Card>
      <CardHeader className="items-center gap-3 text-center">
        <AlertTriangle className="h-10 w-10 text-muted-foreground" />
        <CardTitle>Couldn't load this invoice</CardTitle>
        <CardDescription>Check your connection and try again.</CardDescription>
      </CardHeader>
      <CardContent className="flex justify-center">
        <Button onClick={onRetry}>Retry</Button>
      </CardContent>
    </Card>
  );
}

function PaidCard({
  invoice,
  brand,
}: {
  invoice: PublicInvoiceResponse;
  brand: string;
}) {
  return (
    <BrandedCard brand={brand}>
      <CardHeader className="items-center gap-3 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
        <CardTitle>This invoice has already been paid</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-center text-sm text-muted-foreground">
        <div>Invoice {invoice.documentNumber}</div>
        <div>{formatCurrency(invoice.totalAmount, invoice.currency)}</div>
      </CardContent>
    </BrandedCard>
  );
}

function VoidedCard({
  invoice,
  brand,
}: {
  invoice: PublicInvoiceResponse;
  brand: string;
}) {
  return (
    <BrandedCard brand={brand}>
      <CardHeader className="items-center gap-3 text-center">
        <XCircle className="h-10 w-10 text-muted-foreground" />
        <CardTitle>This invoice has been cancelled</CardTitle>
      </CardHeader>
      <CardContent className="text-center text-sm text-muted-foreground">
        Invoice {invoice.documentNumber}
      </CardContent>
    </BrandedCard>
  );
}

function CreditedCard({
  invoice,
  brand,
}: {
  invoice: PublicInvoiceResponse;
  brand: string;
}) {
  return (
    <BrandedCard brand={brand}>
      <CardHeader className="items-center gap-3 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
        <CardTitle>This invoice has been settled by credit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-center text-sm text-muted-foreground">
        <div>Invoice {invoice.documentNumber}</div>
        {invoice.amountCredited > 0 ? (
          <div>
            {formatCurrency(invoice.totalAmount, invoice.currency)} total ·{" "}
            {formatCurrency(invoice.amountCredited, invoice.currency)} credit applied
          </div>
        ) : null}
        <div>Nothing left to pay.</div>
      </CardContent>
    </BrandedCard>
  );
}

function SucceededCard({
  invoice,
  brand,
}: {
  invoice: PublicInvoiceResponse;
  brand: string;
}) {
  return (
    <BrandedCard brand={brand}>
      <CardHeader className="items-center gap-3 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
        <CardTitle>Payment received</CardTitle>
        <CardDescription>{invoice.organisationName} has been notified.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1 text-center text-sm text-muted-foreground">
        <div>Invoice {invoice.documentNumber}</div>
        <div>{formatCurrency(invoice.totalAmount, invoice.currency)}</div>
      </CardContent>
    </BrandedCard>
  );
}

function InitialisingCheckout() {
  return (
    <div className="flex min-h-[120px] items-center justify-center">
      <Spinner />
    </div>
  );
}

function CheckoutErrorInline({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="space-y-3 text-center">
      <p className="text-sm text-destructive">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          Try again
        </Button>
      )}
    </div>
  );
}

function PaymentForm({
  token,
  session,
  brand,
  onSucceeded,
}: {
  token: string;
  invoice: PublicInvoiceResponse;
  session: CheckoutSessionResponse;
  brand: string;
  onSucceeded: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const payLabel = useMemo(
    () => `Pay ${formatCurrency(session.amount, session.currency)}`,
    [session.amount, session.currency],
  );

  // Post-3DS redirect return: retrieve the PaymentIntent from the URL's
  // client_secret and surface success if it completed on the redirect side.
  useEffect(() => {
    if (!stripe) return;
    const params = new URLSearchParams(window.location.search);
    const secret = params.get("payment_intent_client_secret");
    if (!secret) return;

    let cancelled = false;
    stripe.retrievePaymentIntent(secret).then(({ paymentIntent, error: retrieveError }) => {
      if (cancelled) return;
      if (retrieveError) {
        console.error("retrievePaymentIntent error", { token, error: retrieveError });
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        onSucceeded();
      } else if (paymentIntent?.status === "requires_payment_method") {
        setError("Payment failed. Please try another payment method.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [stripe, token, onSucceeded]);

  async function handleSubmit(e: SyntheticEvent) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/i/${token}`,
      },
      redirect: "if_required",
    });

    if (result.error) {
      console.error("confirmPayment error", { token, stage: "confirm", code: result.error.code });
      setError(result.error.message ?? "Payment failed. Please try again.");
      setSubmitting(false);
      return;
    }

    if (result.paymentIntent?.status === "succeeded") {
      onSucceeded();
      return;
    }

    setError("Payment didn't complete. Please try again.");
    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement onReady={() => setReady(true)} />
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <Button
        type="submit"
        disabled={!stripe || !elements || !ready || submitting}
        className="w-full"
        style={{ backgroundColor: brand, color: pickForeground(brand) }}
      >
        {submitting ? <Spinner className="h-4 w-4" /> : payLabel}
      </Button>
    </form>
  );
}

// Stub for mock mode — renders a Stripe-shaped placeholder so branding can be
// iterated on without mounting real Elements (which would refuse a fake key).
function MockPaymentForm({
  session,
  brand,
  onSucceeded,
}: {
  session: CheckoutSessionResponse;
  brand: string;
  onSucceeded: () => void;
}) {
  const payLabel = `Pay ${formatCurrency(session.amount, session.currency)}`;
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSucceeded();
      }}
    >
      <div className="rounded-md border bg-background p-4 text-sm">
        <div className="mb-2 font-medium">Card information</div>
        <div className="flex h-10 items-center rounded bg-muted/50 px-3 text-muted-foreground">
          4242 4242 4242 4242
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="flex h-10 items-center rounded bg-muted/50 px-3 text-muted-foreground">
            MM / YY
          </div>
          <div className="flex h-10 items-center rounded bg-muted/50 px-3 text-muted-foreground">
            CVC
          </div>
        </div>
        <div className="mt-3 text-xs italic text-muted-foreground">
          Mock payment form — Stripe Payment Element renders here in real mode.
        </div>
      </div>
      <Button
        type="submit"
        className="w-full"
        style={{ backgroundColor: brand, color: pickForeground(brand) }}
      >
        {payLabel}
      </Button>
    </form>
  );
}

